import { useState } from "react"
import { useQuery, useMutation } from "urql"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2, ExternalLink } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { PortalLinksQuery } from "@/lib/graphql/queries"
import { CreatePortalLinkMutation, DeletePortalLinkMutation } from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type PortalLinkItem = ResultOf<typeof PortalLinksQuery>["portalLinks"]["nodes"][number]

const LIMIT = 25

function getScopeLabel(scope: PortalLinkItem["scope"]): string {
  if (scope.applicationId) return "Application"
  if (scope.sourceId) return "Source"
  if (scope.eventId) return "Event"
  if (scope.destinationId) return "Destination"
  return "Global"
}

function getScopeId(scope: PortalLinkItem["scope"]): string {
  return scope.applicationId || scope.sourceId || scope.eventId || scope.destinationId || ""
}

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date()
}

export function PortalLinksPage() {
  const [searchInput, setSearchInput] = useState("")
  const [createOpen, setCreateOpen] = useState(false)
  const [createdUrl, setCreatedUrl] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [search])

  const [result, reexecute] = useQuery({
    query: PortalLinksQuery,
    variables: { search, after: pagination.after, first: pagination.first },
  })
  const [, deletePortalLink] = useMutation(DeletePortalLinkMutation)
  const [, createPortalLink] = useMutation(CreatePortalLinkMutation)

  const [scopeType, setScopeType] = useState("applicationId")
  const [scopeId, setScopeId] = useState("")
  const [expiresIn, setExpiresIn] = useState("24h")
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!deleteId) return
    const res = await deletePortalLink({ id: deleteId })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Portal link revoked")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleCreate = async () => {
    if (!scopeId.trim()) {
      toast.error("Entity ID is required")
      return
    }
    const scope = { [scopeType]: scopeId }
    const res = await createPortalLink({ scope, expiresIn })
    if (res.error) { toast.error(res.error.message); return }
    const token = res.data?.createPortalLink.token
    if (token) {
      const url = `${window.location.origin}/portal?token=${token}`
      setCreatedUrl(url)
    }
    reexecute({ requestPolicy: "network-only" })
  }

  const resetCreateDialog = () => {
    setCreateOpen(false)
    setCreatedUrl("")
    setScopeType("applicationId")
    setScopeId("")
    setExpiresIn("24h")
  }

  const columns: Column<PortalLinkItem>[] = [
    {
      header: "Scope",
      cell: (row) => (
        <Badge variant="outline" className="text-2xs">
          {getScopeLabel(row.scope)}
        </Badge>
      ),
    },
    {
      header: "Entity ID",
      cell: (row) => {
        const id = getScopeId(row.scope)
        return id ? (
          <div className="flex items-center gap-1">
            <code className="text-xs font-mono text-muted-foreground max-w-40 truncate block">{id}</code>
            <CopyButton text={id} />
          </div>
        ) : <span className="text-xs text-muted-foreground">—</span>
      },
    },
    {
      header: "Token",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <code className="text-xs font-mono text-muted-foreground max-w-25 truncate block">
            {row.token.slice(0, 12)}...
          </code>
          <CopyButton text={`${window.location.origin}/portal?token=${row.token}`} />
        </div>
      ),
    },
    {
      header: "Status",
      cell: (row) => (
        <Badge variant={isExpired(row.expiresAt) ? "destructive" : "default"} className="text-2xs">
          {isExpired(row.expiresAt) ? "Expired" : "Active"}
        </Badge>
      ),
    },
    {
      header: "Expires",
      cell: (row) => <TimeAgo date={row.expiresAt} />,
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={(e) => { e.stopPropagation(); setDeleteId(row.id) }}
          aria-label="Revoke portal link"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-12 text-right",
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Portal Links" description="Shareable links for scoped portal access">
        <Button size="sm" className="shadow-primary-glow transition-shadow" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          Create Link
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search portal links..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.portalLinks.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.portalLinks.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No portal links"
        emptyDescription="Create portal links to share scoped access"
      />

      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) resetCreateDialog() }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{createdUrl ? "Portal Link Created" : "Create Portal Link"}</DialogTitle>
          </DialogHeader>

          {createdUrl ? (
            <div className="space-y-4">
              <div className="space-y-2 rounded-lg glass-card p-4">
                <Label>Portal URL</Label>
                <div className="flex items-center gap-2">
                  <Input value={createdUrl} readOnly className="font-mono text-xs" />
                  <CopyButton text={createdUrl} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={resetCreateDialog}>Close</Button>
                <Button onClick={() => window.open(createdUrl, "_blank")}>
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  Open Portal
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-4 rounded-lg glass-card p-4">
                <div className="space-y-2">
                  <Label>Scope Type</Label>
                  <Select value={scopeType} onValueChange={setScopeType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="applicationId">Application</SelectItem>
                      <SelectItem value="sourceId">Source</SelectItem>
                      <SelectItem value="eventId">Event</SelectItem>
                      <SelectItem value="destinationId">Destination</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Entity ID</Label>
                  <Input
                    className="font-mono text-sm"
                    placeholder="UUID of the entity"
                    value={scopeId}
                    onChange={(e) => setScopeId(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expiration</Label>
                  <Select value={expiresIn} onValueChange={setExpiresIn}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1h">1 hour</SelectItem>
                      <SelectItem value="24h">24 hours</SelectItem>
                      <SelectItem value="168h">7 days</SelectItem>
                      <SelectItem value="720h">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={resetCreateDialog}>Cancel</Button>
                <Button onClick={handleCreate}>Create</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Revoke Portal Link"
        description="Are you sure you want to revoke this portal link? Anyone using it will lose access immediately."
        confirmLabel="Revoke"
        onConfirm={handleDelete}
        destructive
      />
    </div>
  )
}
